import { Result, backendApi } from "./backendApi";
import { DataFileModel } from "./vehicleFileAPI";

export interface ReleaseVehicleViewModel {
  recId: number;
  description: string;
  deleted: boolean;
}

export interface ReleaseVehicleFileViewModel {
  number: string;
  dataFiles: DataFileModel[];
  vehicles: ReleaseVehicleViewModel[];
}

export interface UnreleasedReleaseVehicleFileViewModel {
  number: string;
  checkedOutBy: string | undefined;
  dataFiles: DataFileModel[];
  helperFilesValidated: boolean;
  crsFileValidated: boolean;
  modelInfoValidated: boolean;
  isNew: boolean;
  otherDataFileChanges: { [id: string]: number };
  hasModelInfoChanges: boolean;
  isRsa: boolean;
  hasModelInfo: boolean;
  areComponentsSet: boolean;
  hasAutoCadFile: boolean;
  hasCrsFile: boolean;
}

export interface ReleaseViewModel {
  name: string;
  releaseDate: string;
  releaseBy: string;
}

export interface ReleaseDetailsViewModel {
  name: string;
  vehicleFiles: ReleaseVehicleFileViewModel[];
}

export class ReleaseVehicleFileModel {
  constructor(
    public number: string,
    public fireFlag: boolean,
    public rsaFlag: boolean,
  ) {}
}

export class ReleaseModel {
  constructor(public vehicleFiles: ReleaseVehicleFileModel[]) {}
}

class GetUnreleasedVehicleFilesRequest {}

class GetUnreleasedVehicleFilesResponse {
  unreleasedVehicleFiles!: UnreleasedReleaseVehicleFileViewModel[];
}

class GetReleasesRequest {}

class GetReleasesResponse {
  releases!: ReleaseViewModel[];
}

class GetReleaseDetailsRequest {
  name!: string;
  fullRelease!: boolean;
  baseRelease!: string | undefined;
}

class GetReleaseDetailsResponse {
  release!: ReleaseDetailsViewModel;
}

class CreateReleaseRequest {
  constructor(public release: ReleaseModel) {}
}

class CreateReleaseResponse {
  constructor(
    public name: string,
    public legacyName: string,
  ) {}
}

class GetPastReleasesRequest {
  baseRelease!: string;
}

class GetPastReleasesResponse {
  releaseNames!: string[];
}

class GetReleaseFilesRequest {
  releaseName!: string;
}

class GetReleaseFilesResponse {
  files!: string[];
}

class ProcessVehicleFileRequest {
  constructor(
    public vehicleFileNumber: string,
    public skipIfFileExists: boolean,
  ) {}
}

export class ProcessVehicleFileResponse {
  constructor(
    public success: boolean,
    public messages: string[],
  ) {}
}

class PerformPreReleaseValidationsRequest {}

class PerformPreReleaseValidationsResponse {
  constructor(public messages: string[]) {}
}

export const releaseAPI = {
  async performPreReleaseValidations(): Promise<Result<string[]>> {
    let response =
      await backendApi.sendAndGetResponseAsync<PerformPreReleaseValidationsResponse>(
        "PerformPreReleaseValidations",
        new PerformPreReleaseValidationsRequest(),
      );

    if (response.ok) {
      return { ok: true, value: response.value.messages };
    }

    return response;
  },

  async processVehicleFile(
    vehicleFileNumber: string,
    skipIfFileExists: boolean,
  ): Promise<Result<ProcessVehicleFileResponse>> {
    let response =
      await backendApi.sendAndGetResponseAsync<ProcessVehicleFileResponse>(
        "ProcessVehicleFile",
        new ProcessVehicleFileRequest(vehicleFileNumber, skipIfFileExists),
      );

    if (response.ok) {
      return { ok: true, value: response.value };
    }

    return response;
  },
  async getUnreleasedFiles(): Promise<
    Result<UnreleasedReleaseVehicleFileViewModel[]>
  > {
    let response =
      await backendApi.sendAndGetResponseAsync<GetUnreleasedVehicleFilesResponse>(
        "GetUnreleasedVehicleFiles",
        {} as GetUnreleasedVehicleFilesRequest,
      );

    if (response.ok) {
      return { ok: true, value: response.value.unreleasedVehicleFiles };
    }

    return response;
  },
  async getReleases(): Promise<Result<ReleaseViewModel[]>> {
    let response =
      await backendApi.sendAndGetResponseAsync<GetReleasesResponse>(
        "GetReleases",
        {} as GetReleasesRequest,
      );

    if (response.ok) {
      return { ok: true, value: response.value.releases };
    }

    return response;
  },

  async getReleaseDetails(
    name: string,
    fullRelease: boolean,
    baseRelease: string | undefined,
  ): Promise<Result<ReleaseDetailsViewModel>> {
    let response =
      await backendApi.sendAndGetResponseAsync<GetReleaseDetailsResponse>(
        "GetReleaseDetails",
        {
          name: name,
          fullRelease: fullRelease,
          baseRelease: baseRelease,
        } as GetReleaseDetailsRequest,
      );

    if (response.ok) {
      return { ok: true, value: response.value.release };
    }

    return response;
  },

  async createRelease(release: ReleaseModel): Promise<Result<string>> {
    let response =
      await backendApi.sendAndGetResponseAsync<CreateReleaseResponse>(
        "CreateRelease",
        {
          release: release,
        } as CreateReleaseRequest,
      );

    if (response.ok) return { ok: true, value: response.value.name };

    return response;
  },
  async getPastReleases(baseRelease: string): Promise<Result<string[]>> {
    let response =
      await backendApi.sendAndGetResponseAsync<GetPastReleasesResponse>(
        "GetPastReleases",
        {
          baseRelease: baseRelease,
        } as GetPastReleasesRequest,
      );

    if (response.ok) {
      return { ok: true, value: response.value.releaseNames };
    }

    return response;
  },
  async getReleaseFiles(name: string): Promise<Result<string[]>> {
    let response =
      await backendApi.sendAndGetResponseAsync<GetReleaseFilesResponse>(
        "GetReleaseFiles",
        {
          releaseName: name,
        } as GetReleaseFilesRequest,
      );

    if (response.ok) {
      return { ok: true, value: response.value.files };
    }

    return response;
  },
  async downloadReleasePackage(name: string): Promise<any> {
    let response = await backendApi.downloadFileAsync(
      "releasefiles?baseRelease=" + name,
    );

    return response;
  },
};
